import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAlert } from '../Components/AlertContext';
import Breadcrumbs from '../Components/Breadcrumbs';
import Button from '../Components/Buttons';
import { ConfirmationProvider, useConfirmation } from '../Components/ConfirmationContext';
import Table from '../Components/Tables';
import { MarkerApiProvider, useMarkerApi } from '../Services/MarkerApiProvider';
import { formatDateTimeLocal } from '../utils/formatDate';
import useTablePagination from '../Hooks/useTablePagination';

export function MarkersContent({ canEdit }) {
  const { organizationId } = useParams();
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const { showConfirmation } = useConfirmation();
  const { getMarkers, deleteMarker } = useMarkerApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [alertShown, setAlertShown] = useState(false);
  const [items, setItems] = useState([]);
  const [totalItems, setTotalItems] = useState(0);

  const tableTitle = 'Markers';
  const { initialPageSize, storedSkip, storedLimit } = useTablePagination(tableTitle);

  const columns = [
    { name: 'ID', selector: (row) => row.id, sortable: false },
    { name: 'Name', selector: (row) => row.name, sortable: false },
    {
      name: 'Status',
      selector: (row) => (row.is_active === true ? 'Active' : 'InActive'),
      sortable: false,
    },
    { name: 'Created', selector: (row) => formatDateTimeLocal(row.created_at), sortable: false },
    { name: 'Updated', selector: (row) => formatDateTimeLocal(row.updated_at), sortable: false },
  ];

  const handleDelete = (row) => {
    showConfirmation(`Are you sure you want to delete the selected item?`, async () => {
      try {
        setLoading(true);
        await deleteMarker(row);
        await fetchItems(storedSkip(), storedLimit());
        showAlert('success', 'Item was successfully deleted!');
      } catch (error) {
        showAlert('fail', 'Failed to delete the item!');
      } finally {
        setLoading(false);
      }
    });
  };

  const handleRowClicked = (row) => {
    navigate(`/organizations/${organizationId}/markers/${row.id}`, { state: { organizationId } });
  };

  const fetchItems = async (skip, limit) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getMarkers(skip, limit);
      setItems(response.items);
      setTotalItems(response.totalCount);
    } catch (error) {
      setError(error.message);
      showAlert('fail', 'Failed to fetch markers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(storedSkip(), storedLimit());
  }, []);

  return (
    <div className="container">
      <Breadcrumbs />
      <div className="row d-flex gap">
        <div className="radius-yes p-2 bg-yes col-12">
          {error ? (
            <div>Error: {error}</div>
          ) : (
            <>
              <Table
                title={tableTitle}
                columns={columns}
                data={items}
                selectableRows
                selectableRowsHighlight
                paginationPerPage={initialPageSize}
                progressPending={loading}
                paginationTotalRows={totalItems}
                onRowClicked={handleRowClicked}
                onDelete={handleDelete}
                onChangePage={fetchItems}
                getRowUrl={(row) => `/organizations/${organizationId}/markers/${row.id}`}
                searchFields={['name']}
                typeSearch="default"
              />
              {canEdit && (
                <Button
                  background="color2"
                  size="large"
                  onClick={() => navigate(`/organizations/${organizationId}/markers/new-marker`)}
                >
                  Create New Marker
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function Markers({ canEdit }) {
  return (
    <ConfirmationProvider>
      <MarkerApiProvider>
        <MarkersContent canEdit={canEdit} />
      </MarkerApiProvider>
    </ConfirmationProvider>
  );
}

export default Markers;
